// src/components/GuideRequiredModal.js
import React from 'react';
import { useAuth } from '../context/AuthContext'; // Importa el hook useAuth para acceder al usuario

function GuideRequiredModal({ order, onClose, onGoToOrderDetails, onRefreshOrders }) { // Asegura que onRefreshOrders se reciba como prop
  const { user, token } = useAuth(); // <-- AHORA también tomamos token

  // Helper para formatear la guía de Mercado Libre
  const formatMercadoLibreGuide = (guideNumber) => {
    if (!guideNumber) return 'N/A';
    const match = guideNumber.match(/MEL(\d+)(?:LMXDF01|FMDOF01)/);
    if (match && match[1]) {
      return match[1];
    }
    return guideNumber;
  };

  // Función para simular la impresión de una guía y marcarla como impresa en la DB
  const handlePrintAndMarkGuideFromModal = async (guideIdToMark, guideNumberToPrint) => {
    if (!order || !order.id || !guideNumberToPrint || guideNumberToPrint === 'N/A') {
      console.error('Error: No hay pedido o número de guía válido para imprimir desde el modal de requerimiento.');
      return;
    }
    if (!token) {
      alert('Sesión expirada. Vuelve a iniciar sesión.');
      return;
    }

    const performedByUserId = user?.id || null;
    const performedByUsername = user?.nombre || 'Desconocido';

    try {
      // 1. Obtener el PDF de la guía (AGREGADO Authorization)
      const response = await fetch(`/api/orders/${order.id}/label?folios=${encodeURIComponent(order.folio_autoazur)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al obtener el PDF de la guía: ${errorText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Abrir el PDF en una nueva ventana/pestaña para impresión
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          // printWindow.print(); // opcional
        };
      } else {
        console.error('El navegador bloqueó la ventana de impresión desde el modal de requerimiento.');
        alert('El navegador bloqueó la ventana de impresión. Por favor, permite pop-ups para este sitio.');
      }

      // 2. Marcar la guía como impresa en la base de datos (AGREGADO Authorization)
      const markPrintedResponse = await fetch(`/api/orders/${order.id}/print-guide`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          guide_id: guideIdToMark,
          performed_by_user_id: performedByUserId,
          performed_by_username: performedByUsername
        })
      });
      const markPrintedData = await markPrintedResponse.json();
      if (!markPrintedResponse.ok) {
        console.error('Error al marcar guía como impresa después de imprimir (desde modal de requerimiento):', markPrintedData.mensaje);
        alert(`Guía impresa, pero error al marcar como impresa en DB: ${markPrintedData.mensaje}`);
      } else {
        // ¡CORRECCIÓN! Llamar a onRefreshOrders para que el componente padre recargue los pedidos
        onRefreshOrders?.(); 
      }

    } catch (err) {
      console.error('GuideRequiredModal: Error al imprimir guía:', err);
      alert(`Error al imprimir guía: ${err.message}`);
    }
  };

  return (
    // Overlay del modal: cierra al hacer clic fuera
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      {/* Contenido del modal: evita que se cierre al hacer clic dentro */}
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full relative" onClick={(e) => e.stopPropagation()}>
        {/* Botón de cerrar - Ahora fuera del contenido principal */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 bg-gray-800 text-white rounded-full h-8 w-8 flex items-center justify-center text-xl font-bold hover:bg-gray-700 transition duration-200"
          title="Cerrar"
        >
          &times;
        </button>

        <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Acción Requerida para Pedido #{order.referencia_plataforma}</h3>
        <p className="text-gray-700 text-center mb-4">
          Para pasar el pedido a estado <strong>"Preparación"</strong>, es <strong>necesario imprimir una guía de envío válida</strong>.
        </p>

        <div className="bg-gray-100 p-3 rounded-lg mb-4">
          <p className="text-sm text-gray-600 mb-2 font-semibold">Guía(s) Actual(es):</p>
          {order.guides && order.guides.length > 0 ? (
            <div className="flex flex-col items-start">
              {order.guides.map((guide, index) => (
                <div key={guide.id || index} className="flex items-center justify-between w-full text-gray-700 text-base mb-1">
                  <span>
                    {order.canal_origen === 'MERCADO LIBRE' ? formatMercadoLibreGuide(guide.guide_number) : guide.guide_number || 'N/A'}
                    {guide.print_status === 1 ? (
                      <span className="ml-2 text-green-600 font-medium">Impresa &#10003;</span>
                    ) : (
                      <span className="ml-2 text-red-600 font-medium">No Impresa &#10007;</span>
                    )}
                  </span>
                  {/* Botón de Imprimir Guía para cada guía */}
                  {guide.guide_number && guide.guide_number !== 'N/A' && (
                    <button
                      onClick={() => handlePrintAndMarkGuideFromModal(guide.id, guide.guide_number)}
                      className="ml-2 bg-purple-500 hover:bg-purple-600 text-white font-bold py-1 px-2 rounded-lg text-xs transition duration-200"
                      title="Imprimir esta guía y marcarla como impresa"
                    >
                      Imprimir Guía
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-base text-gray-800 font-semibold text-center">No hay guías asociadas.</p>
          )}
        </div>

        <p className="text-gray-700 text-center mb-6">
          Por favor, imprime la guía necesaria. Si necesitas marcarla como impresa o generar una nueva, ve a los detalles del pedido.
        </p>
        <button
          onClick={() => onGoToOrderDetails(order)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline w-full transition duration-200"
        >
          Ir a Detalles del Pedido
        </button>
      </div>
    </div>
  );
}

export default GuideRequiredModal;
